// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
// import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors"; // Store Custom Code
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
// import MobiusLink from "@insite/mobius/Link"; // Foundation
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";

// Store --->
import Hidden from "@insite/mobius/Hidden";
import MobiusLink, { LinkPresentationProps } from "@insite/mobius/Link";
import { linkStyles as mixinsLinkStyles } from "@store/Themes/common/mixins/link";
import { useGetLink } from "@store/Framework/Store/Links/LinksSelectors";
import PinFill from "@store/Library/Components/Icons/Pin/PinFill";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { css } from "styled-components";
// <--- Store

const enum fields {
    destination = "destination",
    overrideTitle = "overrideTitle",
    customCSS = "customCSS",
    // Foundation
    icon = "icon",
    color = "color",
    hasColoredHover = "hasColoredHover",
}

// Foundation --->
const iconMap = {
    none: undefined,
    pin: PinFill,
} as const;

export const enum LinksColors {
    Primary = "primary",
    Secondary = "secondary",
}
// <--- Foundation

interface OwnProps extends WidgetProps {
    fields: {
        [fields.destination]: LinkFieldValue;
        [fields.overrideTitle]: string;
        [fields.customCSS]: string;
        // Foundation
        [fields.icon]: keyof typeof iconMap;
        [fields.color]: LinksColors;
        [fields.hasColoredHover]: boolean;
    };
}

// Foundation --->
export interface BasicLinkStyles {
    routerLink?: LinkPresentationProps;
}

export const basicLinkStyles: BasicLinkStyles = {
    routerLink: {
        icon: {
            iconProps: {
                color: "inherit",
                size: 28,
                css: css`
                    margin-right: ${pxToRem(5)};
                `,
            },
        },
    },
};

const styles = basicLinkStyles;
// <--- Foundation

// const linkStyles: InjectableCss = {}; Foundation
export const Link: FC<OwnProps> = ({ fields }) => {
    const { title, url } = useGetLink(fields.destination);

    // const styles = useMergeStyles("link", linkStyles); Foundation

    if (!url) {
        return null;
    }

    // Foundation
    const icon = iconMap[fields.icon || "none"];
    const { routerLink } = styles;
    const hoverColor = fields.color === "primary" ? "secondary" : "primary";

    const routerLinkStyles = (): LinkPresentationProps => ({
        ...routerLink,
        css: fields.hasColoredHover ? mixinsLinkStyles(fields.color, hoverColor, false, false) : undefined,
    });
    // Foundation

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <MobiusLink
                href={url}
                // Foundation
                {...routerLinkStyles()}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
            >
                {/* Foundation */}
                {icon ? (
                    <Hidden below="lg">{fields.overrideTitle || title || url}</Hidden>
                ) : (
                    fields.overrideTitle || title || url
                )}
                {/* Foundation */}
            </MobiusLink>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Link,
    definition: {
        group: "Basic",
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },

            // Foundation --->
            {
                name: fields.icon,
                displayName: "Link Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Pin",
                        value: "pin",
                    },
                ],
                defaultValue: "none",
                fieldType: "General",
            },
            {
                name: fields.color,
                displayName: "Link Color",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Secondary",
                        value: LinksColors.Secondary,
                    },
                    {
                        displayName: "Primary",
                        value: LinksColors.Primary,
                    },
                ],
                defaultValue: LinksColors.Secondary,
                fieldType: "General",
            },
            {
                name: fields.hasColoredHover,
                displayName: "Make the link colored on hover",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            // <--- Foundation
        ],
    },
};

export default widgetModule;
